import {useRef} from 'react';
import {toIntent} from '../../intent';
import {onRender} from '../intentTypes';
import {useIsomorphicLayoutEffect} from '../../util';
import {useCurrentTimeline} from '../../animate/timeline';
import {NodeAnimation} from '../../animate/animate';

import type {Intent} from '../../intent';
import type {
  KeyframeDescription,
  KeyframeStyles,
  KeyframeOptions,
  AnimationTask,
} from '../../animate/animate';

type AnimationIntent = Intent<{ref: React.RefObject<HTMLElement>}>;

export function normalizeKeyframe(keyframe: KeyframeDescription): {
  styles: KeyframeStyles;
  options: KeyframeOptions;
} {
  const {delay, duration, easing, ...styles} = keyframe;
  return {
    styles: styles as KeyframeStyles,
    options: {delay, duration, easing},
  };
}

function refAnimation(
  animationRef: React.MutableRefObject<NodeAnimation>,
  styles: KeyframeStyles,
  options: KeyframeOptions,
): AnimationIntent {
  return onRender(() => {
    const ref = useRef<HTMLElement>(null);
    const timeline = useCurrentTimeline();

    useIsomorphicLayoutEffect(() => {
      if (animationRef.current === null) {
        animationRef.current = new NodeAnimation(
          timeline,
          ref,
          styles,
          options,
        );
      }
    }, [timeline]);

    return [{ref}];
  });
}

function conditionalAnimation(
  condition: boolean,
  styles: KeyframeStyles,
  options: KeyframeOptions,
): AnimationIntent {
  return onRender(() => {
    const ref = useRef<HTMLElement>(null);
    const pushedTask = useRef<AnimationTask | null>(null);
    const timeline = useCurrentTimeline();

    const animationRef = useRef<NodeAnimation | null>(null);

    useIsomorphicLayoutEffect(() => {
      if (animationRef.current === null) {
        animationRef.current = new NodeAnimation(
          timeline,
          ref,
          styles,
          options,
        );
      }

      const animation = animationRef.current;

      if (condition) {
        if (pushedTask.current == null) {
          pushedTask.current = animation.play({push: true});
        }
      } else if (ref.current && pushedTask.current != null) {
        pushedTask.current.removeAndPlay();
        pushedTask.current = null;
      }
    });

    return [{ref}];
  });
}

function presenceAnimation(
  keyframe: KeyframeDescription,
  type: 'in' | 'out',
  reverse?: 'keyframes' | 'effects',
): AnimationIntent {
  const {styles, options} = normalizeKeyframe(keyframe);
  if (reverse) {
    options.reverse = reverse;
  }

  return onRender(() => {
    const ref = useRef<HTMLElement>(null);
    const timeline = useCurrentTimeline();

    useIsomorphicLayoutEffect(() => {
      const animation = new NodeAnimation(timeline, ref, styles, options);
      return timeline.register(type, animation);
    }, [timeline]);

    return [{ref}];
  });
}

function isTestEnv(): boolean {
  if (process.env.NODE_ENV === 'test') {
    return true;
  }

  return false;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noopIntent = onRender(() => {}) as AnimationIntent;

export function transition(keyframe: KeyframeDescription): {
  when: (condition: boolean) => AnimationIntent;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ref: (ref: any) => AnimationIntent;
} {
  const {styles, options} = normalizeKeyframe(keyframe);

  return {
    when: (condition) => {
      if (isTestEnv()) {
        return noopIntent;
      }
      return conditionalAnimation(condition, styles, options);
    },
    ref: (ref) => {
      if (isTestEnv()) {
        return noopIntent;
      }
      return refAnimation(ref, styles, options);
    },
  };
}

export function transitionIn(keyframe: KeyframeDescription): AnimationIntent {
  if (isTestEnv()) {
    return noopIntent;
  }

  return presenceAnimation(keyframe, 'in', 'keyframes');
}

export function transitionOut(keyframe: KeyframeDescription): AnimationIntent {
  if (isTestEnv()) {
    return noopIntent;
  }

  return presenceAnimation(keyframe, 'out');
}

export function transitionInOut(
  keyframe: KeyframeDescription,
): AnimationIntent {
  if (isTestEnv()) {
    return noopIntent;
  }

  return toIntent([
    presenceAnimation(keyframe, 'in', 'keyframes'),
    presenceAnimation(keyframe, 'out', 'effects'),
  ]);
}
